// IM-XPR-002 (IM-UI-093, IM-UX-110) — Peças comuns da tela de um objetivo: cabeçalho, visão do processo (posição, sem
// percentual), seleção da fase na URL (`?fase=`, deep link e voltar do navegador), detalhes recolhidos com as etapas do
// motor em linguagem de tela e o início do objetivo. Nenhuma regra de etapa aqui: tudo vem de `JourneyDto`.
import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { macroProgress, STEP_REASON_LABEL, STEP_STATE_LABEL, type JourneyDto, type MacroPhaseView, type ObjectiveDef, type StepId } from '@intelimove/contracts';
import { ApiError } from '../api/client';
import { useToast } from '../components/Toast';
import { journeysApi } from '../data/journeys';
import { useCanManage } from '../pages/Targets';
import { stepPlain } from './data';
import { ContextLink } from './returnTo';

/** Fase selecionada (URL) ou, sem escolha válida, a fase atual. Trocar de fase não empilha histórico. */
export function usePhase(keys: readonly string[], fallback: string): [string, (k: string) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get('fase');
  const selected = raw && keys.includes(raw) ? raw : fallback;
  const set = (k: string) => { const p = new URLSearchParams(params); p.set('fase', k); setParams(p, { replace: true }); };
  return [selected, set];
}

export function ObjectiveHead({ def, journey, children, hideAnalyses = false }: { def: ObjectiveDef; journey: JourneyDto | null; children?: ReactNode; hideAnalyses?: boolean }) {
  return (
    <div className="xp-process-head">
      <div>
        <Link className="xp-back" to="/objetivos">← Meus objetivos</Link>
        <p className="xp-eyebrow">{journey ? (journey.journey.lifecycle === 'PAUSADA' ? 'Objetivo pausado' : journey.journey.lifecycle === 'EM_ANDAMENTO' ? 'Objetivo em andamento' : 'Objetivo em acompanhamento') : 'Objetivo'}</p>
        <h1 id="page-title" className="xp-title">{def.title}</h1>
        <p className="xp-lead">{def.outcome}</p>
      </div>
      <div className="xp-process-actions">
        {children}
        {/* IM-XPR-006: no objetivo de empresas a ação principal é a carteira; "todas as análises" fica em Análises e dados. */}
        {!hideAnalyses && <Link className="secondary btn-link" to="/analises">Ver todas as análises</Link>}
      </div>
    </div>
  );
}

/** "Você está na fase 2 de 5" + etapas essenciais em número (nunca porcentagem). */
export function ProcessOverview({ phases, journey, extra }: { phases: readonly MacroPhaseView[] | null; journey: JourneyDto | null; extra?: ReactNode }) {
  const pos = phases ? macroProgress(phases) : null;
  const current = phases?.find((p) => p.current);
  return (
    <div className="xp-overview">
      <div>
        {journey && pos?.position ? <b>Você está na fase {pos.position} de {pos.total}{current ? ` · ${current.name}` : ''}</b> : <b>{journey ? 'Todas as fases foram percorridas' : 'Ainda não começou'}</b>}
        <span>{journey ? `${journey.progress.requiredDone} de ${journey.progress.requiredApplicable} etapas essenciais concluídas · ${pos?.settled ?? 0} de ${pos?.total ?? 0} fases concluídas` : 'Ao começar, o InteliMove passa a mostrar onde você está e o que falta.'}</span>
      </div>
      {extra}
    </div>
  );
}

export function StartButton({ def, onStarted }: { def: ObjectiveDef; onStarted: () => void }) {
  const canManage = useCanManage();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  if (!canManage) return <span className="cell-sub">Um administrador da organização pode começar este objetivo.</span>;
  return (
    <button type="button" className="xp-primary" disabled={busy} aria-busy={busy} onClick={async () => {
      setBusy(true);
      try { const r = await journeysApi.create({ type: def.journeyType }); toast(r.created ? 'Objetivo iniciado.' : 'O objetivo já estava em andamento.'); onStarted(); }
      catch (e) { toast(e instanceof ApiError ? e.message : 'Não foi possível começar agora.', 'error'); }
      finally { setBusy(false); }
    }}>{busy ? 'Começando…' : 'Começar este objetivo'}</button>
  );
}

const DONE = new Set(['CONCLUIDA', 'CONCLUIDA_COM_LIMITACOES', 'DESATUALIZADA']);
/** Primeira etapa aberta da fase com tela própria (a ação principal quando a decisão não cabe no fluxo). */
export function openStepOf(journey: JourneyDto | null, ids: readonly StepId[]) {
  if (!journey) return null;
  const steps = ids.map((id) => journey.steps.find((s) => s.stepId === id)).filter((s): s is JourneyDto['steps'][number] => !!s && s.state !== 'DISPENSADA');
  return steps.find((s) => s.kind !== 'OPCIONAL' && !DONE.has(s.state) && s.href) ?? steps.find((s) => !DONE.has(s.state) && s.href) ?? null;
}

/** Detalhes recolhidos: as etapas da fase, o motivo do estado, o que falta (com o link certo) e o detalhe técnico. */
export function StepsDetail({ journey, ids, back }: { journey: JourneyDto | null; ids: readonly StepId[]; back: { to: string; label: string } }) {
  if (!journey) return null;
  const steps = ids.map((id) => journey.steps.find((s) => s.stepId === id)).filter((s): s is JourneyDto['steps'][number] => !!s);
  if (!steps.length) return null;
  return (
    <details className="xp-why">
      <summary>Como o InteliMove chegou até aqui</summary>
      <ul className="xp-steps-detail">
        {steps.map((s) => (
          <li key={s.stepId}>
            <span><b>{stepPlain(s.stepId, s.title)}</b>{s.kind === 'OPCIONAL' ? ' (opcional)' : s.kind === 'CONDICIONAL' ? ' (quando se aplica)' : ''}<br />
              <span className="cell-sub">{s.skipped ? s.skipped.reason : s.reading === 'INDISPONIVEL' ? STEP_REASON_LABEL.LEITURA_INDISPONIVEL : STEP_REASON_LABEL[s.reason]}{s.artifact ? ` · Base: ${s.artifact.label}` : ''}</span>
              {s.missing.length > 0 && <span className="cell-sub"> · {s.missing.slice(0, 3).map((m, i) => <span key={m.key}>{i ? '; ' : ''}{m.route ? <ContextLink className="link-btn" to={m.route} back={back}>{m.label}</ContextLink> : m.label}</span>)}</span>}
            </span>
            <span className="tag gray">{STEP_STATE_LABEL[s.state]}</span>
          </li>
        ))}
      </ul>
      <p className="cell-sub">Metodologia, evidências e histórico completos ficam na <ContextLink className="link-btn" to={`/jornadas/${journey.journey.id}`} back={back}>visão detalhada das etapas</ContextLink>.</p>
    </details>
  );
}

